import { trackClientEvent } from "@/src/lib/analytics/client"
import { ANALYTICS_ACTIONS, ANALYTICS_CATEGORIES } from "@/src/lib/analytics/events"
import type { AnalyticsAction, AnalyticsCategory, AnalyticsEventInput } from "@/src/lib/analytics/events"

const CLICK_SELECTOR =
  "[data-analytics-category], [data-analytics-action], [data-analytics-name], a, button, [role='button']"

const CHANGE_SELECTOR = "[data-analytics-category], [data-analytics-action], [data-analytics-name], input, select, textarea"

function normalizeName(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 120)
}

function getElementName(element: HTMLElement): string {
  const datasetName = element.dataset.analyticsName
  if (datasetName) {
    return normalizeName(datasetName)
  }

  const ariaLabel = element.getAttribute("aria-label")
  if (ariaLabel) {
    return normalizeName(ariaLabel)
  }

  const title = element.getAttribute("title")
  if (title) {
    return normalizeName(title)
  }

  if (element instanceof HTMLAnchorElement && element.href) {
    return normalizeName(element.href)
  }

  return normalizeName(element.textContent || element.id || element.className || "unknown_control")
}

function isSocialHost(url: URL): boolean {
  return ["linkedin.com", "www.linkedin.com", "bsky.app", "reddit.com", "www.reddit.com"].includes(url.hostname)
}

function inferEventFromElement(element: HTMLElement): Pick<AnalyticsEventInput, "category" | "action"> {
  const explicitCategory = element.dataset.analyticsCategory as AnalyticsCategory | undefined
  const explicitAction = element.dataset.analyticsAction as AnalyticsAction | undefined

  if (explicitCategory && explicitAction) {
    return { category: explicitCategory, action: explicitAction }
  }

  if (explicitCategory) {
    return { category: explicitCategory, action: explicitAction || ANALYTICS_ACTIONS.CLICK }
  }

  if (explicitAction) {
    return { category: ANALYTICS_CATEGORIES.UTILITY, action: explicitAction }
  }

  if (element instanceof HTMLAnchorElement) {
    try {
      const targetUrl = new URL(element.href, window.location.origin)

      if (targetUrl.origin !== window.location.origin) {
        if (isSocialHost(targetUrl)) {
          return { category: ANALYTICS_CATEGORIES.SOCIAL_ENGAGEMENT, action: ANALYTICS_ACTIONS.CLICK }
        }

        return { category: ANALYTICS_CATEGORIES.OUTBOUND_LINK, action: ANALYTICS_ACTIONS.CLICK }
      }

      if (targetUrl.pathname.startsWith("/app")) {
        return { category: ANALYTICS_CATEGORIES.AUTH_NAV, action: ANALYTICS_ACTIONS.CLICK }
      }

      return { category: ANALYTICS_CATEGORIES.PUBLIC_NAV, action: ANALYTICS_ACTIONS.CLICK }
    } catch {
      return { category: ANALYTICS_CATEGORIES.UTILITY, action: ANALYTICS_ACTIONS.CLICK }
    }
  }

  const ariaLabel = element.getAttribute("aria-label")?.toLowerCase() || ""
  if (ariaLabel.includes("mode") || ariaLabel.includes("theme")) {
    return { category: ANALYTICS_CATEGORIES.THEME_PREFERENCE, action: ANALYTICS_ACTIONS.TOGGLE }
  }

  return { category: ANALYTICS_CATEGORIES.UTILITY, action: ANALYTICS_ACTIONS.CLICK }
}

function buildEvent(element: HTMLElement): AnalyticsEventInput {
  const inferred = inferEventFromElement(element)

  return {
    category: inferred.category,
    action: inferred.action,
    name: getElementName(element),
  }
}

function shouldSkipElement(element: HTMLElement): boolean {
  if (element.getAttribute("data-analytics-disabled") === "true") {
    return true
  }

  if (element instanceof HTMLButtonElement) {
    return element.disabled
  }

  return element.getAttribute("aria-disabled") === "true"
}

function handleClick(event: MouseEvent): void {
  const target = event.target as HTMLElement | null
  if (!target) {
    return
  }

  const element = target.closest<HTMLElement>(CLICK_SELECTOR)
  if (!element || shouldSkipElement(element)) {
    return
  }

  trackClientEvent(buildEvent(element))
}

function handleChange(event: Event): void {
  const target = event.target as HTMLElement | null
  if (!target) {
    return
  }

  const element = target.closest<HTMLElement>(CHANGE_SELECTOR)
  if (!element || shouldSkipElement(element)) {
    return
  }

  const defaultEvent = buildEvent(element)
  const isToggleControl =
    (element instanceof HTMLInputElement && ["checkbox", "radio"].includes(element.type)) ||
    element.getAttribute("role") === "switch" ||
    element.getAttribute("aria-pressed") !== null

  trackClientEvent({
    ...defaultEvent,
    action:
      (element.dataset.analyticsAction as AnalyticsAction | undefined) ||
      (isToggleControl ? ANALYTICS_ACTIONS.TOGGLE : ANALYTICS_ACTIONS.SUBMIT),
  })
}

function handleCopy(event: ClipboardEvent): void {
  const target = event.target as HTMLElement | null
  if (!target) {
    return
  }

  const element = target.closest<HTMLElement>(CHANGE_SELECTOR)
  if (!element || shouldSkipElement(element)) {
    return
  }

  const defaultEvent = buildEvent(element)
  trackClientEvent({
    ...defaultEvent,
    action: (element.dataset.analyticsAction as AnalyticsAction | undefined) || ANALYTICS_ACTIONS.COPY,
  })
}

export function bindGlobalAnalyticsListeners(): () => void {
  document.addEventListener("click", handleClick, true)
  document.addEventListener("change", handleChange, true)
  document.addEventListener("copy", handleCopy, true)

  return () => {
    document.removeEventListener("click", handleClick, true)
    document.removeEventListener("change", handleChange, true)
    document.removeEventListener("copy", handleCopy, true)
  }
}
